import { Avatar, Box, Container, Grid, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import aboutImg from "../../../assets/images/icons/about-shvr.jpg";
import aboutImgMobile from "../../../assets/images/icons/about-shvr-mobile.jpg";
import aboutBgImg from "../../../assets/images/icons/about-bg.png";
import objectiveImg from "../../../assets/images/icons/about-objectives.jpg";
import objectiveImgMobile from "../../../assets/images/icons/about-objectives-mobile.jpg";
import Methodology from "./methodology/methodology";

interface Objective {
  title: string;
  description: string;
}

const AboutSHVR = () => {
  const theme = useTheme();
  const { t } = useTranslation("about");
  const isDarkMode = theme.palette.mode === "dark";
  const points = t("points", { returnObjects: true }) as string[];
  const objectives = t("objectives.items", {
    returnObjects: true,
  }) as Objective[];

  const pageBackground = isDarkMode
    ? `linear-gradient(rgba(26, 29, 33, 0.94), rgba(26, 29, 33, 0.97)), url(${aboutBgImg})`
    : `linear-gradient(rgba(240, 255, 242, 0.82), rgba(247, 255, 247, 0.9)), url(${aboutBgImg})`;

  return (
    <>
      <Box
        component="main"
        sx={{
          py: { xs: 6, md: 10 },
          color: "text.primary",
          backgroundImage: pageBackground,
          backgroundPosition: "center top",
          backgroundRepeat: "repeat-y",
          backgroundSize: "cover",
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2.5, md: 6, lg: 9 } }}>
          <Grid
            container
            spacing={{ xs: 4, md: 5 }}
            alignItems="center"
            sx={{ mb: { xs: 8, md: 12 } }}
          >
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                component="img"
                src={aboutImg}
                srcSet={`${aboutImgMobile} 767w, ${aboutImg} 1200w`}
                sizes="(max-width: 767px) 100vw, 500px"
                alt="Students participating in a clean and green school activity"
                sx={{
                  display: "block",
                  width: "100%",
                  height: { xs: 340, sm: 470, md: 570 },
                  objectFit: "cover",
                  borderRadius: { xs: "22px", md: "30px" },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                component="h1"
                sx={{
                  mb: 2,
                  color: isDarkMode ? "#f5f7f6" : "#0f202b",
                  fontSize: {
                    xs: `calc(28px * var(--dynamic-font-size, 1))`,
                    md: `calc(40px * var(--dynamic-font-size, 1))`,
                  },
                  fontWeight: 700,
                  lineHeight: 1.15,
                }}
              >
                {t("title")}
              </Typography>

              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: "none",
                  color: isDarkMode ? "#d7ddda" : "#19251f",
                }}
              >
                {points.map((point, index) => (
                  <Typography
                    component="li"
                    key={index}
                    sx={{
                      mb: 0.8,
                      fontSize: {
                        xs: `calc(14px * var(--dynamic-font-size, 1))`,
                        md: `calc(16px * var(--dynamic-font-size, 1))`,
                      },
                      lineHeight: 1.65,
                      "&::before": {
                        content: '"•"',
                        mr: 0.8,
                        color: isDarkMode ? "#b8b8b8" : "#1A5F20",
                      },
                    }}
                  >
                    {point}
                  </Typography>
                ))}
              </Box>
            </Grid>
          </Grid>

          <Box component="section" aria-labelledby="objectives-heading">
            <Typography
              id="objectives-heading"
              component="h2"
              sx={{
                mb: { xs: 4, md: 5 },
                color: isDarkMode ? "#f5f7f6" : "#0f202b",
                fontSize: {
                  xs: `calc(28px * var(--dynamic-font-size, 1))`,
                  md: `calc(40px * var(--dynamic-font-size, 1))`,
                },
                fontWeight: 700,
                lineHeight: 1.15,
              }}
            >
              {t("objectives.title")}
            </Typography>

            <Grid container spacing={{ xs: 5, md: 7 }} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                {objectives.map((objective, index) => (
                  <Box
                    key={objective.title}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      mb: index === objectives.length - 1 ? 0 : 3,
                    }}
                  >
                    <Avatar
                      aria-hidden="true"
                      sx={{
                        width: 38,
                        height: 38,
                        flex: "0 0 38px",
                        bgcolor: isDarkMode ? "#747474" : "#1A5F20",
                        color: "#fff",
                        fontSize: "1rem",
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Avatar>
                    <Box>
                      <Typography
                        component="h3"
                        sx={{
                          mb: 0.35,
                          color: isDarkMode ? "#f5f7f6" : "#101813",
                          fontSize: `calc(16px * var(--dynamic-font-size, 1))`,
                          fontWeight: 700,
                          lineHeight: 1.35,
                        }}
                      >
                        {objective.title}
                      </Typography>
                      <Typography
                        sx={{
                          maxWidth: 600,
                          color: isDarkMode ? "#c8cfcb" : "#26342b",
                          fontSize: {
                            xs: `calc(14px * var(--dynamic-font-size, 1))`,
                            md: `calc(16px * var(--dynamic-font-size, 1))`,
                          },
                          lineHeight: 1.5,
                        }}
                      >
                        {objective.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  component="img"
                  src={objectiveImg}
                  srcSet={`${objectiveImgMobile} 767w, ${objectiveImg} 1200w`}
                  sizes="(max-width: 767px) 100vw, 500px"
                  alt="Clean and green school campus"
                  sx={{
                    display: "block",
                    width: "100%",
                    height: { xs: 330, sm: 460, md: 520 },
                    objectFit: "cover",
                    borderRadius: { xs: "22px", md: "30px" },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      <Methodology />
    </>
  );
};

export default AboutSHVR;
